import { formatarCnpj } from '@fiscoai/nfe/identificadores';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const NUMERO = new Intl.NumberFormat('pt-BR');
const DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Valor fiscal chega como string decimal ("1234.50"): só vira número para exibir. */
export function formatarMoeda(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  const numero = Number(valor);
  return Number.isFinite(numero) ? BRL.format(numero) : valor;
}

export function formatarNumero(valor: number): string {
  return NUMERO.format(valor);
}

/**
 * Datas fiscais são mostradas COMO VIERAM no texto, sem converter fuso: a nota emitida às 23:30 em
 * Manaus (-04:00) aparece 23:30, e a data local do relatório da SEFAZ (sem fuso) não "anda". Só
 * instantes do próprio sistema (UTC, terminados em `Z`) são convertidos para o horário de quem vê.
 */
const TEXTO_DATA = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;

interface Partes {
  data: string;
  hora: string | null;
}

function partesDoInstante(iso: string): Partes | null {
  const instante = new Date(iso);
  if (Number.isNaN(instante.getTime())) return null;
  const [data = '', hora = ''] = DATA_HORA.format(instante).split(', ');
  return { data, hora };
}

function partesDoTexto(iso: string): Partes | null {
  const m = TEXTO_DATA.exec(iso);
  if (!m) return null;
  const [, ano = '', mes = '', dia = '', h, min] = m;
  return { data: `${dia}/${mes}/${ano}`, hora: h && min ? `${h}:${min}` : null };
}

function partes(iso: string): Partes | null {
  return iso.endsWith('Z') ? partesDoInstante(iso) : partesDoTexto(iso);
}

export function formatarData(iso: string | null | undefined): string {
  if (!iso) return '—';
  return partes(iso)?.data ?? iso;
}

export function formatarDataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const p = partes(iso);
  if (!p) return iso;
  return p.hora ? `${p.data} ${p.hora}` : p.data;
}

/** CNPJ (14, inclusive alfanumérico) ou CPF (11) com máscara; outros valores como vieram. */
export function formatarDocumento(documento: string | null | undefined): string {
  if (!documento) return '—';
  if (documento.length === 14) return formatarCnpj(documento);
  if (/^\d{11}$/.test(documento)) {
    return `${documento.slice(0, 3)}.${documento.slice(3, 6)}.${documento.slice(6, 9)}-${documento.slice(9)}`;
  }
  return documento;
}

/** "1,2 MB" para tamanhos de arquivo. */
export function formatarBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const unidades = ['KB', 'MB', 'GB'];
  let valor = bytes / 1024;
  let i = 0;
  while (valor >= 1024 && i < unidades.length - 1) {
    valor /= 1024;
    i++;
  }
  return `${valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${unidades[i] ?? 'GB'}`;
}

/** Iniciais para avatar: "Ana Paula Souza" → "AS". */
export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  const primeira = partes[0]?.charAt(0) ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.charAt(0) ?? '') : '';
  return `${primeira}${ultima}`.toUpperCase();
}

/** "7122000000" → "(71) 2200-0000"; celular com 9 dígitos; o que não reconhece fica como veio. */
export function formatarTelefone(telefone: string | null | undefined): string {
  if (!telefone) return '—';
  const d = telefone.replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return telefone;
}

/** "42707650" → "42707-650". */
export function formatarCep(cep: string | null | undefined): string {
  if (!cep) return '—';
  const d = cep.replace(/\D/g, '');
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : cep;
}
